import { useQuery } from '@tanstack/react-query'
import { MonthlyScore } from '../services/types'
import attitudesService from '../services/attitudesService'
import { formatMonth, formatDateTime } from '../utils/formatters'

interface ScoreDetailsModalProps {
  score: MonthlyScore
  employeeName?: string
  isOpen: boolean
  onClose: () => void
}

export const ScoreDetailsModal = ({
  score,
  employeeName,
  isOpen,
  onClose,
}: ScoreDetailsModalProps) => {
  // Fetch all attitudes to show details
  const { data: attitudes = [] } = useQuery({
    queryKey: ['attitudes'],
    queryFn: () => attitudesService.getAll(),
  })

  if (!isOpen) return null

  // Get selected attitudes
  const selectedAttitudes = attitudes.filter((a) =>
    score.selected_attitudes.includes(a.id)
  )

  // Separate by type
  const positiveAttitudes = selectedAttitudes.filter((a) => a.type === 'positive')
  const negativeAttitudes = selectedAttitudes.filter((a) => a.type === 'negative')

  // Calculate breakdown
  const highestPositive = positiveAttitudes.length > 0
    ? Math.max(...positiveAttitudes.map((a) => a.points))
    : 0

  const totalNegative = negativeAttitudes.reduce((sum, a) => sum + a.points, 0)

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-card shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 z-10">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">
                  Detalhes da Avaliação
                </h2>
                {employeeName && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {employeeName} • {formatMonth(score.month, score.year)}
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-6 space-y-6">
            {/* Status Badge */}
            {score.is_reversed && (
              <div className="bg-danger/10 border border-danger/20 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <p className="font-semibold text-danger">Esta avaliação foi revertida</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Os pontos desta avaliação não são contabilizados.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Points Summary */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Pontuação
              </h3>

              <div className="space-y-3">
                {/* Positive */}
                {positiveAttitudes.length > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">
                      Maior atitude positiva:
                    </span>
                    <span className="text-lg font-semibold text-success">
                      +{highestPositive}
                    </span>
                  </div>
                )}

                {/* Negative */}
                {negativeAttitudes.length > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">
                      Total de atitudes negativas:
                    </span>
                    <span className="text-lg font-semibold text-danger">
                      {totalNegative}
                    </span>
                  </div>
                )}

                {/* Total */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                  <span className="font-semibold text-gray-900 dark:text-white">
                    Total:
                  </span>
                  <span className={`text-2xl font-bold ${
                    score.is_reversed
                      ? 'text-gray-500 line-through'
                      : score.points_snapshot > 0
                      ? 'text-success'
                      : score.points_snapshot < 0
                      ? 'text-danger'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}>
                    {score.points_snapshot > 0 ? '+' : ''}{score.points_snapshot}
                  </span>
                </div>
              </div>
            </div>

            {/* Selected Attitudes */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Atitudes Selecionadas
              </h3>

              {/* Positive */}
              {positiveAttitudes.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Positivas
                  </h4>
                  <div className="space-y-2">
                    {positiveAttitudes.map((attitude) => (
                      <div
                        key={attitude.id}
                        className="flex items-center justify-between p-3 bg-success/5 border border-success/20 rounded-lg"
                      >
                        <span className="text-gray-900 dark:text-white">
                          {attitude.name}
                        </span>
                        <span className="text-success font-semibold">
                          +{attitude.points}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Negative */}
              {negativeAttitudes.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Negativas
                  </h4>
                  <div className="space-y-2">
                    {negativeAttitudes.map((attitude) => (
                      <div
                        key={attitude.id}
                        className="flex items-center justify-between p-3 bg-danger/5 border border-danger/20 rounded-lg"
                      >
                        <span className="text-gray-900 dark:text-white">
                          {attitude.name}
                        </span>
                        <span className="text-danger font-semibold">
                          {attitude.points}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Observation */}
            {score.observation && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Observações
                </h3>
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {score.observation}
                  </p>
                </div>
              </div>
            )}

            {/* Metadata */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 dark:text-gray-500 mb-1">Criado em</p>
                  <p className="text-gray-900 dark:text-white">
                    {formatDateTime(score.created_at)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-500 mb-1">Atualizado em</p>
                  <p className="text-gray-900 dark:text-white">
                    {formatDateTime(score.updated_at)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-6">
            <button
              onClick={onClose}
              className="w-full py-3 px-4 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-medium rounded-btn transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default ScoreDetailsModal
